import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { useModal } from '@hooks/app/modal.hook'
import { useFormModel } from '@hooks/form-model/form-model.hook'
import { useUpdateFormModel } from '@hooks/form-model/update-form-model.hook'

import { Modal } from '@components/Modal'
import { Refresh } from '@components/Refresh'

import { FormField, FormModelValidationConfigModel, LocalFile } from '@types'

import { FormBuilder } from '../../components/FormBuilder/FormBuilder.component'
import { fieldsAreValid, transformFormModelStructureToInput } from '../../components/FormBuilder/FormBuilder.helper'

import { Container } from './edit-form-model.styled'

export const EditFormModelPage = () => {
  const { hideModal } = useModal()
  const { t } = useTranslation()

  const { projectId } = useParams()

  const [searchParams] = useSearchParams()

  const formModelId = searchParams.get('formModelId')

  const { updateFormModel } = useUpdateFormModel()

  const { data, loading, error, refetch } = useFormModel(formModelId || '')

  const isLoading = loading ? 1 : error ? -1 : 0
  const formModel = data?.formModel

  const [title, setTitle] = useState<string>('')
  const [fields, setFields] = useState<FormField[]>([])
  const [validationConfig, setValidationConfig] = useState<FormModelValidationConfigModel>()
  const [localFiles, setLocalFiles] = useState<Record<string, LocalFile[]>>({})

  useEffect(() => {
    if (formModel) {
      setTitle(formModel.title)
      setFields(formModel.structure.fields)
      setValidationConfig(formModel.structure.validationConfig)
    }
  }, [formModel])

  const handleOnSubmit = () => {
    if (formModelId && validationConfig) {
      updateFormModel(
        {
          id: formModelId,
          title,
          structure: transformFormModelStructureToInput(fields, validationConfig),
        },
        localFiles,
      )

      hideModal('update_form_model')
    }
  }

  const formIsValid = fieldsAreValid(fields)
  const isDisabled = !formIsValid

  return (
    <Modal
      title={t('modules_project-settings_pages_edit_form-model_title')}
      onSubmitLabel={t('modules_project-settings_pages_edit_form_model_button_edit_form')}
      onClose={() => hideModal('update_form_model')}
      onSubmit={handleOnSubmit}
      disabledSubmitButton={isDisabled}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(formModel?.id || '')}>
          {formModel && projectId && (
            <FormBuilder
              fields={fields}
              title={title}
              projectId={projectId}
              validationConfig={validationConfig}
              setValidationConfig={setValidationConfig}
              setLocalFiles={setLocalFiles}
              setFields={setFields}
              setTitle={setTitle}
            />
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
